// The run of a sculpted bench (docs/claude/lattice.md): the chain of faces it
// stands against — room walls (a heater wall's face where one stands, as the
// benches see it), and a partition's face at either end — and the bench's own
// frame over it: `along` mm from its start, `out` mm from the faces into the
// room, in the room's local plan. Corners between faces are rounded at depth:
// the back keeps the room's own corner (sharp, or its rounded curve), while
// deeper lines curve round it with at least the bench's corner radius, so a cage
// spanning a corner bends the bench round it smoothly. `along` is measured on
// the line halfway through the bench's depth, piece by piece (each face and
// each corner its own piece), so a station stays over its wall at every depth.
// Pure.

import type {
  ProductInstance,
  SculptedBench,
  SculptEnd,
  SculptHost,
  Vec2,
} from '../shared/types';
import { backrestFace } from './backrest';
import { wallThicknessOf } from './dent';
import {
  faceRadius,
  innerWallSpan,
  isPolyRoom,
  polyBenchEdges,
  RADIUS_EPS,
  roomCorners,
  wallView,
} from './polyRoom';

/** A face of the run as it walks: `o + t·p` is the point at run parameter `p`
 *  on the face's line, `n` points into the room. A stored station `s` is
 *  `p = sign · s` (a partition's face B walks back toward its wall). `lo` / `hi`
 *  bound the parameter where the face is straight. */
export interface RunFace { host: SculptHost; o: Vec2; t: Vec2; n: Vec2; sign: 1 | -1; lo: number; hi: number }

/** The corner between two faces of the run: the room's own back radius `r0`,
 *  whether it turns toward the room (`convex`: the inside of a room corner) or
 *  away (the notch of an L), and how far it turns (radians). */
export interface RunCorner { r0: number; convex: boolean; phi: number }

/** A run resolved: its faces, the corners between them, its start and end
 *  parameters on the first and last faces. `blocked`: a partition stands across
 *  it, so it stops at that partition's face. */
export interface RunChain { faces: RunFace[]; corners: RunCorner[]; p0: number; p1: number; blocked?: boolean }

const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
const mul = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k });
const crossZ = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;

/** Where the lines through `a` along `u` and through `b` along `v` cross. */
function meet(a: Vec2, u: Vec2, b: Vec2, v: Vec2): Vec2 | null {
  const den = crossZ(u, v);
  if (Math.abs(den) < 1e-12) return null;
  const t = crossZ(sub(b, a), v) / den;
  return add(a, mul(u, t));
}

/** Two hosts the same face. */
export function sameSculptHost(a: SculptHost, b: SculptHost): boolean {
  if ('wall' in a) return 'wall' in b && a.wall === b.wall;
  return 'partition' in b && a.partition === b.partition && a.face === b.face;
}

/** A host's face as the run walks it, or null when it no longer resolves. A
 *  wall's straight stretch leaves its rounded corners' curves out. */
export function sculptFace(inst: ProductInstance, host: SculptHost): RunFace | null {
  const f = backrestFace(inst, host);
  if (!f) return null;
  if ('partition' in host) {
    const sign = host.face === 'b' ? -1 : 1;
    const t = mul(f.u, sign);
    return { host, o: f.o, t, n: f.n, sign, lo: sign > 0 ? f.lo : -f.hi, hi: sign > 0 ? f.hi : -f.lo };
  }
  const view = wallView(inst);
  const n = view.polygon?.length ?? 0;
  const corners = roomCorners(view);
  const edge = polyBenchEdges(view)[host.wall];
  // A rounded corner keeps its curve: the straight face runs between its tangent stations.
  const cs = corners[host.wall], ce = corners[(host.wall + 1) % n];
  const lo = cs ? Math.max(f.lo, cs.station) : f.lo;
  const hi = ce && edge ? Math.min(f.hi, edge.len - ce.station) : f.hi;
  return { host, o: f.o, t: f.u, n: f.n, sign: 1, lo, hi: Math.max(lo, hi) };
}

/** A face's point at parameter `p`, `d` mm into the room. */
export function facePointAt(f: RunFace, p: number, d: number): Vec2 {
  return add(add(f.o, mul(f.t, p)), mul(f.n, d));
}

/** The parameter of a stored station on a face, and back. */
export const paramOf = (f: RunFace, s: number): number => f.sign * s;
export const stationOf = (f: RunFace, p: number): number => f.sign * p;

/** The partitions standing on wall `w`: each one's id and the stations of its two faces. */
function partitionsOn(inst: ProductInstance, w: number): Array<{ id: string; s: number; t: number }> {
  const view = wallView(inst);
  const out: Array<{ id: string; s: number; t: number }> = [];
  for (const iw of view.innerWalls ?? []) {
    if (iw.edge !== w || !iw.id || !(iw.length > 0)) continue;
    const sp = innerWallSpan(view, iw);
    if (sp) out.push({ id: iw.id, s: sp.s, t: sp.t });
  }
  return out.sort((a, b) => a.s - b.s);
}

/** The wall a partition stands on. */
function partitionWall(inst: ProductInstance, id: string): number | null {
  const iw = wallView(inst).innerWalls?.find((w) => w.id === id);
  return iw?.edge ?? null;
}

/** The corner where face `a` hands over to face `b`. */
function cornerBetween(inst: ProductInstance, a: RunFace, b: RunFace): RunCorner {
  const phi = Math.acos(Math.max(-1, Math.min(1, dot(a.t, b.t))));
  const convex = dot(b.t, a.n) > 0;
  let r0 = 0;
  if ('wall' in a.host && 'wall' in b.host) {
    const view = wallView(inst);
    const n = view.polygon?.length ?? 0;
    const c = roomCorners(view)[(a.host.wall + 1) % n];
    if (c) r0 = Math.max(0, faceRadius(c, wallThicknessOf(view)));
    if (r0 < RADIUS_EPS) r0 = 0;
  }
  return { r0, convex, phi };
}

/**
 * The faces a run walks from `start` to `end`: along a wall in its direction,
 * round its corners onto the next walls; from a partition's face B onto the wall
 * at its foot, and from a wall onto a partition's face A, where it ends. Never
 * round a partition's free end (a run on face A or ending on B goes nowhere but
 * its own face). A partition standing across the walls it would walk stops it
 * there (`blocked`). Null when an end no longer resolves or the run has no length.
 */
export function runChain(inst: ProductInstance, start: SculptEnd, end: SculptEnd): RunChain | null {
  if (!isPolyRoom(inst)) return null;
  const f0 = sculptFace(inst, start.host), f1 = sculptFace(inst, end.host);
  if (!f0 || !f1) return null;
  const clampP = (f: RunFace, p: number): number => Math.max(f.lo, Math.min(f.hi, p));
  if (sameSculptHost(start.host, end.host)) {
    let p0 = clampP(f0, paramOf(f0, start.s)), p1 = clampP(f0, paramOf(f0, end.s));
    if (p1 < p0) [p0, p1] = [p1, p0];
    if ('wall' in f0.host && !(p1 - p0 > 1)) return null;
    if ('wall' in f0.host) {
      // A partition standing between the two ends stops the run at its face.
      const block = partitionsOn(inst, f0.host.wall).find((q) => q.s > p0 && q.s < p1);
      if (block) return { faces: [f0], corners: [], p0, p1: block.s, blocked: true };
    }
    return p1 - p0 > 1 ? { faces: [f0], corners: [], p0, p1 } : null;
  }
  // A run on a partition's face A, or one ending on a face B, would turn round its free end.
  if ('partition' in start.host && start.host.face === 'a') return null;
  if ('partition' in end.host && end.host.face === 'b') return null;
  const view = wallView(inst);
  const n = view.polygon?.length ?? 0;
  if (n < 3) return null;
  const firstWall = 'wall' in start.host ? start.host.wall : partitionWall(inst, start.host.partition);
  const lastWall = 'wall' in end.host ? end.host.wall : partitionWall(inst, end.host.partition);
  if (firstWall == null || lastWall == null) return null;
  // Where the run comes onto its first wall and leaves its last (outer-edge stations).
  const enterAt = 'wall' in start.host ? start.s : partitionsOn(inst, firstWall).find((q) => q.id === (start.host as { partition: string }).partition)?.t;
  const leaveAt = 'wall' in end.host ? end.s : partitionsOn(inst, lastWall).find((q) => q.id === (end.host as { partition: string }).partition)?.s;
  if (enterAt == null || leaveAt == null) return null;
  let steps = (lastWall - firstWall + n) % n;
  if (steps === 0 && leaveAt <= enterAt + 1) steps = n;
  const faces: RunFace[] = [];
  if ('partition' in start.host) faces.push(f0);
  let blocked = false, stopAt: number | null = null;
  for (let k = 0; k <= steps; k++) {
    const w = (firstWall + k) % n;
    const f = sculptFace(inst, { wall: w });
    if (!f) return null;
    const from = k === 0 ? enterAt : -Infinity, to = k === steps ? leaveAt : Infinity;
    const block = partitionsOn(inst, w).find((q) => q.s > from + 0.5 && q.s < to - 0.5 && !(k === steps && 'partition' in end.host && q.id === end.host.partition));
    faces.push(f);
    if (block) { blocked = true; stopAt = block.s; break; }
  }
  if (!blocked && 'partition' in end.host) faces.push(f1);
  const corners: RunCorner[] = [];
  for (let i = 0; i + 1 < faces.length; i++) corners.push(cornerBetween(inst, faces[i], faces[i + 1]));
  const first = faces[0], last = faces[faces.length - 1];
  const p0 = 'partition' in start.host ? clampP(first, paramOf(first, start.s)) : clampP(first, start.s);
  const p1 = blocked ? stopAt! : 'partition' in end.host ? clampP(last, paramOf(last, end.s)) : clampP(last, end.s);
  return { faces, corners, p0, p1, ...(blocked ? { blocked } : {}) };
}

/** A corner resolved at one depth: the arc's centre and radius and its two
 *  tangent points, with their parameters on the face before and after. */
interface CornerAt { c: Vec2; rho: number; ta: Vec2; tb: Vec2; pa: number; pb: number; sweep: number }

/** One piece of the run at a depth: a face's straight stretch or a corner's arc. */
type PieceAt =
  | { kind: 'face'; face: RunFace; a: number; b: number }
  | { kind: 'corner'; corner: CornerAt; face: RunFace };

/** A run with its frame: `at(along, out)` is the room-local plan point;
 *  `length` the run's length on its middle line (`along` runs 0 → length). */
export interface SculptRun {
  chain: RunChain;
  /** The bench's depth (the profile's), the middle line at half of it. */
  depth: number;
  cornerRadius: number;
  length: number;
  at(along: number, out: number): Vec2;
  /** The unit direction the run walks at `along`, `out` deep. */
  dir(along: number, out: number): Vec2;
  /** The frame point nearest a plan point (Newton from the best of a coarse scan). */
  locate(p: Vec2, guess?: { along: number; out: number }): { along: number; out: number };
  /** Each piece's span of `along` (face and corner pieces, in order)… */
  breaks: number[];
  /** …and whether each is a corner. */
  corners: boolean[];
}

/** The radius the front takes round a convex corner at depth `d`: the room's
 *  own curve where that is larger, else the bench's corner radius, growing from
 *  the back (none) to the front (all of it). */
function convexRadius(r0: number, d: number, depth: number, cornerRadius: number): number {
  const dd = Math.max(0, d);
  return Math.max(r0 - dd, depth > 0 ? (cornerRadius * dd) / depth : 0, 0);
}

/** The run's pieces `d` deep. */
function piecesAt(chain: RunChain, d: number, depth: number, cornerRadius: number): PieceAt[] {
  const { faces, corners } = chain;
  // Each corner's tangent points; first the radius it would take, then clamped
  // so neighbouring corners and the run's ends still leave straight faces.
  const raw = corners.map((k, i) => {
    const a = faces[i], b = faces[i + 1];
    if (k.phi < 1e-6) return null;
    const rho = k.convex ? convexRadius(k.r0, d, depth, cornerRadius) : k.r0 + Math.max(0, d);
    // The tangent length off the depth-d lines' crossing.
    return { a, b, rho, tl: rho * Math.tan(k.phi / 2), k };
  });
  // The crossing of the depth-d lines, as a parameter on each face.
  const vertex = raw.map((r) => {
    if (!r) return null;
    const v = meet(add(r.a.o, mul(r.a.n, d)), r.a.t, add(r.b.o, mul(r.b.n, d)), r.b.t);
    return v ? { v, pa: dot(sub(v, r.a.o), r.a.t), pb: dot(sub(v, r.b.o), r.b.t) } : null;
  });
  const startP = (i: number): number => (i === 0 ? chain.p0 : vertex[i - 1]?.pb ?? faces[i].lo);
  const endP = (i: number): number => (i === faces.length - 1 ? chain.p1 : vertex[i]?.pa ?? faces[i].hi);
  const avail = faces.map((_f, i) => Math.max(0, endP(i) - startP(i)));
  const out: PieceAt[] = [];
  let p = chain.p0;
  for (let i = 0; i < faces.length; i++) {
    const f = faces[i];
    if (i < corners.length) {
      const r = raw[i], v = vertex[i];
      if (!r || !v) { out.push({ kind: 'face', face: f, a: p, b: endP(i) }); p = startP(i + 1); continue; }
      // A convex corner takes what fits: half a face shared with another corner, all of one ending the run.
      let tl = r.tl;
      if (r.k.convex) {
        const shareA = i === 0 ? avail[i] : avail[i] / 2, shareB = i + 1 === faces.length - 1 ? avail[i + 1] : avail[i + 1] / 2;
        tl = Math.min(tl, shareA, shareB);
      }
      const rho = r.k.phi > 1e-6 ? tl / Math.tan(r.k.phi / 2) : 0;
      // Convex: the arc's centre is `rho` further into the room; reflex: toward the wall.
      const sg = r.k.convex ? 1 : -1;
      const c = meet(add(r.a.o, mul(r.a.n, d + sg * rho)), r.a.t, add(r.b.o, mul(r.b.n, d + sg * rho)), r.b.t) ?? v.v;
      const ta = sub(c, mul(r.a.n, sg * rho)), tb = sub(c, mul(r.b.n, sg * rho));
      const pa = dot(sub(ta, f.o), f.t), pb = dot(sub(tb, r.b.o), r.b.t);
      const sweep = (crossZ(r.a.t, r.b.t) >= 0 ? 1 : -1) * r.k.phi;
      out.push({ kind: 'face', face: f, a: p, b: pa });
      out.push({ kind: 'corner', face: f, corner: { c, rho, ta, tb, pa, pb, sweep } });
      p = pb;
    } else {
      out.push({ kind: 'face', face: f, a: p, b: chain.p1 });
    }
  }
  return out;
}

const pieceLength = (pc: PieceAt): number => (pc.kind === 'face' ? Math.max(0, pc.b - pc.a) : Math.abs(pc.corner.sweep) * pc.corner.rho);

/** A piece's point at fraction `f` along it, `d` deep (its own depth already in it). */
function piecePoint(pc: PieceAt, f: number, d: number): Vec2 {
  if (pc.kind === 'face') return facePointAt(pc.face, pc.a + (pc.b - pc.a) * f, d);
  const { c, ta, rho, sweep } = pc.corner;
  if (rho < 1e-9) return ta;
  const a0 = Math.atan2(ta.y - c.y, ta.x - c.x) + sweep * f;
  return { x: c.x + rho * Math.cos(a0), y: c.y + rho * Math.sin(a0) };
}

/** The frame of a sculpted bench's run, or null when its run does not resolve. */
export function sculptRun(inst: ProductInstance, sb: Pick<SculptedBench, 'start' | 'end' | 'cornerRadius'>, depth: number): SculptRun | null {
  const chain = runChain(inst, sb.start, sb.end);
  if (!chain) return null;
  const D = Math.max(1, depth);
  const R = sb.cornerRadius != null && sb.cornerRadius >= 0 ? sb.cornerRadius : D;
  const mid = D / 2;
  const ref = piecesAt(chain, mid, D, R);
  const breaks = [0];
  for (const pc of ref) breaks.push(breaks[breaks.length - 1] + pieceLength(pc));
  const length = breaks[breaks.length - 1];
  const cache = new Map<number, PieceAt[]>();
  const pieces = (d: number): PieceAt[] => {
    const key = Math.round(d * 1000) / 1000;
    let p = cache.get(key);
    if (!p) { if (cache.size > 4096) cache.clear(); cache.set(key, p = piecesAt(chain, key, D, R)); }
    return p;
  };
  const at = (along: number, out: number): Vec2 => {
    const ps = pieces(out);
    // Past an end, the end's face runs straight on.
    if (along <= 0 || along >= length) {
      const endFirst = along <= 0;
      const pc = endFirst ? ps[0] : ps[ps.length - 1];
      const f = endFirst ? chain.faces[0] : chain.faces[chain.faces.length - 1];
      const p = endFirst ? (pc.kind === 'face' ? pc.a : chain.p0) : (pc.kind === 'face' ? pc.b : chain.p1);
      return facePointAt(f, p + (endFirst ? along : along - length), out);
    }
    let k = 0;
    while (k + 1 < ps.length && breaks[k + 1] < along) k++;
    const span = breaks[k + 1] - breaks[k];
    return piecePoint(ps[k], span > 1e-9 ? (along - breaks[k]) / span : 0, out);
  };
  const dir = (along: number, out: number): Vec2 => {
    const h = 0.5;
    const a = at(along - h, out), b = at(along + h, out);
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return { x: (b.x - a.x) / l, y: (b.y - a.y) / l };
  };
  const locate = (p: Vec2, guess?: { along: number; out: number }): { along: number; out: number } => {
    let best = guess ?? { along: 0, out: mid };
    if (!guess) {
      // A coarse scan for the nearest middle-line point, its depth off the face.
      let bestErr = Infinity;
      const steps = Math.max(8, Math.ceil(length / 40));
      for (let s = 0; s <= steps; s++) {
        const a = (length * s) / steps;
        const back = at(a, 0), front = at(a, D);
        const nx = front.x - back.x, ny = front.y - back.y, nl = Math.hypot(nx, ny) || 1;
        const v = sub(p, back);
        const o = (v.x * nx + v.y * ny) / nl, side = Math.abs(v.x * ny - v.y * nx) / nl;
        if (side < bestErr) { bestErr = side; best = { along: a, out: (o / nl) * D }; }
      }
    }
    // Newton on (along, out) ↦ plan.
    for (let it = 0; it < 30; it++) {
      const q = at(best.along, best.out);
      const ex = p.x - q.x, ey = p.y - q.y;
      if (Math.hypot(ex, ey) < 1e-4) break;
      const h = 0.25;
      const qa = at(best.along + h, best.out), qo = at(best.along, best.out + h);
      const j11 = (qa.x - q.x) / h, j21 = (qa.y - q.y) / h, j12 = (qo.x - q.x) / h, j22 = (qo.y - q.y) / h;
      const det = j11 * j22 - j12 * j21;
      if (Math.abs(det) < 1e-12) break;
      best = { along: best.along + (j22 * ex - j12 * ey) / det, out: best.out + (-j21 * ex + j11 * ey) / det };
    }
    return best;
  };
  return { chain, depth: D, cornerRadius: R, length, at, dir, locate, breaks, corners: ref.map((pc) => pc.kind === 'corner') };
}

/** How far in front of a face the Sculpted bench tool still takes it (mm). */
export const SCULPT_REACH = 900;

/** The face nearest a room-local point within reach, and its station there: a
 *  wall's straight face, or a partition's. */
export function sculptHostAt(inst: ProductInstance, p: Vec2, reach = SCULPT_REACH): SculptEnd | null {
  if (!isPolyRoom(inst)) return null;
  const view = wallView(inst);
  const hosts: SculptHost[] = [];
  const n = view.polygon?.length ?? 0;
  for (let w = 0; w < n; w++) hosts.push({ wall: w });
  for (const iw of view.innerWalls ?? []) if (iw.edge != null && iw.id && iw.length > 0) hosts.push({ partition: iw.id, face: 'a' }, { partition: iw.id, face: 'b' });
  let best: SculptEnd | null = null, bestD = reach;
  for (const host of hosts) {
    const f = sculptFace(inst, host);
    if (!f) continue;
    const v = sub(p, f.o);
    const d = dot(v, f.n);
    if (d < -1 || d > bestD) continue;
    const pp = dot(v, f.t);
    if (pp < f.lo - 1 || pp > f.hi + 1) continue;
    bestD = d;
    best = { host, s: stationOf(f, Math.max(f.lo, Math.min(f.hi, pp))) };
  }
  return best;
}
